/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1738540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Feb 03 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "bloomreach",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Customers",
          "depth": 1,
          "id": "customers"
        }, {
          "value": "Catalogs",
          "depth": 1,
          "id": "catalogs"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      ol: "ol",
      li: "li",
      h3: "h3",
      a: "a",
      code: "code",
      img: "img",
      h4: "h4",
      ul: "ul"
    }, _provideComponents(), props.components), {PremiumDestination, EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Bloomreach"
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to any events"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customers"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to the customer objects"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Catalogs"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to catalog items"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "To use the Bloomreach destination in Hightouch, you need to provide a Bloomreach API Key, API Secret, API URL, and Project Token. Follow these steps to retrieve your credentials:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In Bloomreach, click the wheel icon in the top right corner. Select ", _jsx(_components.strong, {
            children: "Project Settings"
          }), " from the dropdown."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Access management"
          }), " in the side navigation. Select ", _jsx(_components.strong, {
            children: "API"
          }), " from the options."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your ", _jsx(_components.strong, {
            children: "Project token"
          }), " and ", _jsx(_components.strong, {
            children: "API Base URL"
          }), " are here. You can either use an existing ", _jsx(_components.strong, {
            children: "API Key ID"
          }), " and ", _jsx(_components.strong, {
            children: "API Secret"
          }), ", or you can click ", _jsx(_components.strong, {
            children: "+ Add key"
          }), " to create another one."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing events, Hightouch treats any records added to ", _jsx(_components.a, {
          href: "/getting-started/concepts",
          children: "your source"
        }), " as new events and sends them to Bloomreach when your sync runs."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsxs(_components.p, {
        children: ["You need to select the type of event and one or more hard and/or soft customer IDs to make the API call to Bloomreach.\nThe most common customer IDs are ", _jsx(_components.code, {
          children: "registered"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-bloomreach-events-mappings.png",
          alt: "Selecting columns to send as events to Bloomreach"
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "One or more of the event properties, also known as attributes, are required. The event properties show up as custom mappings and attach custom data relevant to your event."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-bloomreach-events-custom-mappings.png",
          alt: "Mapping event properties in the Hightouch UI"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Custom mappings are included as the ", _jsx(_components.a, {
            href: "https://documentation.bloomreach.com/engagement/reference/add-event-2#body-parameters",
            children: _jsx(_components.code, {
              children: "properties"
            })
          }), "\nparameter of the API request payload."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about event properties, refer to ", _jsx(_components.a, {
          href: "https://documentation.bloomreach.com/engagement/reference/add-event-2",
          children: "Bloomreach's documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "customers",
        children: "Customers"
      }), "\n", _jsxs(_components.p, {
        children: ["You can also sync to the ", _jsx(_components.strong, {
          children: "Customers"
        }), " object. This integration supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, meaning you can insert new customers and update existing ones in Bloomreach."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with customers in Bloomreach on one or more of the following hard and/or soft customer IDs:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Cookie"
        }), "\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "Registered"
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["ID mappings are included as the ", _jsx(_components.a, {
            href: "https://documentation.bloomreach.com/engagement/reference/update-customer-properties-2",
            children: _jsx(_components.code, {
              children: "customer_ids"
            })
          }), "\nparameter of the API request payload."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You need to include at least customer property you wish to update. The customer properties show up as custom mappings and attach custom data relevant to your customer."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-bloomreach-objects-custom-mappings.png",
          alt: "Mapping customer properties in the Hightouch UI"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Custom mappings are included as the ", _jsx(_components.a, {
            href: "https://documentation.bloomreach.com/engagement/reference/update-customer-properties-2",
            children: _jsx(_components.code, {
              children: "properties"
            })
          }), "\nparameter of the API request payload."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about customer properties, refer to ", _jsx(_components.a, {
          href: "https://documentation.bloomreach.com/engagement/reference/update-customer-properties-2",
          children: "Bloomreach's documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "catalogs",
        children: "Catalogs"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync data to Bloomreach ", _jsx(_components.strong, {
          children: "Catalogs"
        }), " to manage product or item data. This integration supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, allowing you to insert new catalog items and update existing ones."]
      }), "\n", _jsx(_components.h4, {
        id: "catalog-selection",
        children: "Catalog selection"
      }), "\n", _jsx(_components.p, {
        children: "You can either create a new catalog or sync to an existing catalog in Bloomreach. When creating a new catalog, Hightouch automatically creates the catalog with fields based on your mappings."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You must match rows in your model with catalog items using the ", _jsx(_components.strong, {
          children: "Item ID"
        }), " field. This uniquely identifies each item in the catalog."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Map columns from your source to catalog item properties. These properties contain the data for each item in your catalog, such as product name, price, or description."
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about catalogs, refer to ", _jsx(_components.a, {
          href: "https://documentation.bloomreach.com/engagement/reference/catalog-api-overview-2",
          children: "Bloomreach's documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
